import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { camerasApi } from '@/api/endpoints'
import { SwitchRow } from '@/components/cameras/detection/SwitchRow'
import { toast } from '@/lib/toast'
import type { Camera } from '@/types'

/**
 * Item 408 — the camera's Beta switch (superusers only; the sheet gates it, the server refuses everyone else). While on,
 * the appearance clues run on this camera. It saves on its own and moves at once: while the request is out it shows what
 * was asked; after a failure it shows the last known value again, with a toast. The camera prop stays stale while the
 * sheet is open, so the server's last answer is kept here.
 */
export function CameraCluesBetaSwitch({ camera }: { camera: Camera }) {
  const { t } = useTranslation('cameras')
  const queryClient = useQueryClient()
  const [answered, setAnswered] = useState<boolean | null>(null)

  const save = useMutation({
    mutationFn: (enabled: boolean) => camerasApi.setCluesBeta(camera.id, enabled),
    onSuccess: (res) => {
      setAnswered(res.clues_beta_enabled)
      toast.success(t(res.clues_beta_enabled ? 'cluesBeta.savedOn' : 'cluesBeta.savedOff'))
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      queryClient.invalidateQueries({ queryKey: ['all-cameras'] })
      queryClient.invalidateQueries({ queryKey: ['camera'] })
    },
    onError: () => toast.error(t('cluesBeta.failed')),
  })

  const known = answered ?? camera.clues_beta_enabled === true
  return (
    <section>
      <SwitchRow
        id={`camera-clues-beta-${camera.id}`}
        label={t('cluesBeta.label')}
        description={t('cluesBeta.hint')}
        checked={save.isPending && save.variables !== undefined ? save.variables : known}
        onCheckedChange={(enabled) => save.mutate(enabled)}
        disabled={save.isPending}
      />
    </section>
  )
}
